import type { QueryClient } from "react-query";
import { Id, toast } from "react-toastify";
import { fetchItem, postItem } from "@/queries/queries-helper";
import ENDPOINTS from "@/services/endpoints";
import JobStatus from "@/models/enums/job-status";
import JobDto, { prodTaskAdapter } from "@/models/interfaces/dtos/production/job-dto";
import type { JobCreatedDto } from "@/models/interfaces/dtos/production/job-created-dto";
import type ResponseApi from "@/models/interfaces/response-api";
import pollJob from "@/hooks/jobs/poll-job";
import processCallbacks, { ProcessProject } from "./project-process-callbacks";
import { readSavedProcesses, removeSavedProcess, SavedProcess, saveProcess } from "./project-process-storage";

export interface ProductionProcessOptions {
    newBoomerRevision?: boolean;
}

export type ProductionProcessResult = { succeeded: false } | { succeeded: true; jobId: string; toastId: Id; isExistingJob: boolean };

interface RunnerSnapshot {
    userId?: string;
    activeProjects: ReadonlySet<string>;
    jobs: ReadonlyMap<string, JobDto>;
    errors: ReadonlyMap<string, Error>;
}

const isRunningJob = (job: JobDto | null | undefined): job is JobDto =>
    job != null && (job.status === JobStatus.Pending || job.status === JobStatus.Processing);

const createProjectProcessRunner = (queryClient: QueryClient) => {
    let generation = 0;
    let snapshot: RunnerSnapshot = { activeProjects: new Set(), jobs: new Map(), errors: new Map() };
    const listeners = new Set<() => void>();
    const counts = new Map<string, number>();
    const tasks = new Map<string, { record: SavedProcess; promise: Promise<ProductionProcessResult> }>();
    const launches = new Map<string, { processName: string; promise: Promise<ProductionProcessResult> }>();
    const completed = new Map<string, ProductionProcessResult>();

    const publish = (next: Partial<RunnerSnapshot>) => {
        snapshot = { ...snapshot, ...next };
        listeners.forEach(listener => listener());
    };
    const setBusy = (projectId: string, change: number) => {
        const count = (counts.get(projectId) ?? 0) + change;
        if (count > 0) counts.set(projectId, count);
        else counts.delete(projectId);
        publish({ activeProjects: new Set(counts.keys()) });
    };
    const updateJob = (projectId: string, job: JobDto) => publish({ jobs: new Map(snapshot.jobs).set(projectId, job) });
    const reportError = (projectId: string, error: unknown) => {
        const failure = error instanceof Error ? error : new Error("Le traitement du projet a échoué.");
        publish({ errors: new Map(snapshot.errors).set(projectId, failure) });
        return failure;
    };
    const refreshProjectData = async (project: ProcessProject) => {
        const refreshQuery = async (key: string[]) => {
            // Une lecture démarrée avant la fin du calcul peut encore renvoyer les anciennes données.
            await queryClient.cancelQueries(key, { exact: true });
            await queryClient.invalidateQueries(key, { exact: true });
        };

        // Le statut détermine si les requêtes d'indicateurs et de fichiers Boomer sont activées.
        await refreshQuery(["dashboard-process-status", project.projectId]);

        const keys = [
            ["indicators-def", project.projectId],
            ["files-path", project.projectId],
            ["project-measures", project.projectId],
            ["project-baamer-file-revisions", project.projectId],
            ["project-measure-types", project.projectId],
            ["packs-baamer-file-revision", project.baamerRevisionId],
            ["files-revision-baamer-revision", project.baamerRevisionId],
        ];
        await Promise.all(keys.map(refreshQuery));
    };
    const pendingJob = (jobId: string, name: string): JobDto => ({
        jobId, workParameters: { name }, status: JobStatus.Pending,
        startTime: new Date(), lastUpdate: new Date(), progressInformation: "", progressPercentage: 0,
    });

    const follow = (record: SavedProcess, initialJob: JobDto, isExistingJob = false): Promise<ProductionProcessResult> => {
        const { userId } = snapshot;
        if (!userId) return Promise.resolve({ succeeded: false });
        const key = `${record.projectId}:${record.jobId}`;
        const existing = tasks.get(key);
        if (existing) return existing.promise;
        if (completed.has(key)) return Promise.resolve(completed.get(key)!);
        const runGeneration = generation;
        const isActive = () => generation === runGeneration && snapshot.userId === userId;
        setBusy(record.projectId, 1);
        updateJob(record.projectId, initialJob);
        saveProcess(userId, record);

        const promise = (async () => {
            const toastId = toast.loading(`${record.processName} : traitement en cours...`);
            try {
                const result = await pollJob(async () => {
                    const status = await fetchItem<JobDto>(ENDPOINTS.PRODUCTION_GET_JOB_STATUS(record.jobId));
                    if (isActive() && status) updateJob(record.projectId, status);
                    return status;
                }, prodTaskAdapter.getStatus, isActive);
                if (!result || !isActive()) return { succeeded: false } as const;
                queryClient.setQueryData(["project-process", record.projectId], null);
                if (result.status !== JobStatus.Success) throw new Error("Le processus de production a échoué.");

                await processCallbacks.get(record.processName)?.({
                    project: record,
                    isActive,
                    sendCalculatedData: async ignoreValidated => {
                        if (!isActive()) return;
                        toast.update(toastId, { render: "Envoi des données...", isLoading: true });
                        const response = await postItem<ResponseApi<JobCreatedDto>>(ENDPOINTS.BOOMER_LAUNCH_POST_REVISION(record.projectId), {
                            projectBoomerId: record.projectBoomerId,
                            newBoomerRevision: record.newBoomerRevision,
                            ignoreValidated,
                        });
                        if (!isActive()) return;
                        if (!response?.success || !response.entity?.jobId) throw new Error(response?.errorMessages?.[0] ?? "L'envoi des données n'a pas pu être lancé.");
                        updateJob(record.projectId, pendingJob(response.entity.jobId, "Envoi Boomer"));
                        const sent = await pollJob(async () => {
                            const status = await fetchItem<JobDto>(ENDPOINTS.BOOMER_GET_POST_REVISION_STATUS(response.entity.jobId));
                            if (isActive() && status) updateJob(record.projectId, status);
                            return status;
                        }, prodTaskAdapter.getStatus, isActive);
                        if (isActive() && sent?.status !== JobStatus.Success) throw new Error("L'envoi des données a échoué.");
                    },
                });
                if (!isActive()) return { succeeded: false } as const;
                await refreshProjectData(record);
                if (!isActive()) return { succeeded: false } as const;
                const outcome: ProductionProcessResult = { succeeded: true, jobId: record.jobId, toastId, isExistingJob };
                completed.set(key, outcome);
                toast.update(toastId, { render: "Traitement terminé.", type: "success", isLoading: false, autoClose: 5000, closeButton: true });
                return outcome;
            } catch (error) {
                if (isActive()) {
                    completed.set(key, { succeeded: false });
                    const failure = reportError(record.projectId, error);
                    toast.update(toastId, { render: failure.message, type: "error", isLoading: false, autoClose: false, closeButton: true });
                }
                return { succeeded: false } as const;
            } finally {
                if (isActive()) {
                    tasks.delete(key);
                    removeSavedProcess(userId, record.jobId);
                    const jobs = new Map(snapshot.jobs);
                    jobs.delete(record.projectId);
                    publish({ jobs });
                    setBusy(record.projectId, -1);
                } else toast.dismiss(toastId);
            }
        })();
        tasks.set(key, { record, promise });
        return promise;
    };

    const launch = (project: ProcessProject, processName: string, jobUrl: string, options?: ProductionProcessOptions): Promise<ProductionProcessResult> => {
        if (!snapshot.userId) return Promise.resolve({ succeeded: false });
        const pending = launches.get(project.projectId);
        const running = Array.from(tasks.values()).find(task => task.record.projectId === project.projectId);
        if (pending || running) {
            const name = pending?.processName ?? running?.record.processName;
            if (name === processName) return pending?.promise ?? running!.promise;
            toast.error("Un autre traitement est déjà en cours sur ce projet.");
            return Promise.resolve({ succeeded: false });
        }
        const { userId } = snapshot;
        const runGeneration = generation;
        const isActive = () => generation === runGeneration && snapshot.userId === userId;
        const errors = new Map(snapshot.errors);
        errors.delete(project.projectId);
        publish({ errors });
        setBusy(project.projectId, 1);

        const promise = (async () => {
            try {
                const previous = await fetchItem<JobDto>(ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(project.projectId));
                if (!isActive()) return { succeeded: false } as const;
                if (isRunningJob(previous)) {
                    const saved = readSavedProcesses(userId).find(item => item.projectId === project.projectId && item.jobId === previous.jobId);
                    // Un job d'un autre onglet/utilisateur ne doit pas déclencher notre suite métier.
                    if (!saved || saved.processName !== processName || previous.workParameters?.name !== processName) {
                        throw new Error("Un processus est déjà en cours sur ce projet. Son suivi est affiché sans lancer un nouveau traitement.");
                    }
                    const succeeded = await follow(saved, previous, true);
                    return succeeded;
                }
                const response = await postItem<ResponseApi<JobCreatedDto>>(jobUrl);
                if (!isActive()) return { succeeded: false } as const;
                if (!response?.success || !response.entity?.jobId) throw new Error(response?.errorMessages?.[0] ?? "Le lancement du processus a échoué.");
                const record: SavedProcess = {
                    ...project, newBoomerRevision: options?.newBoomerRevision ?? project.newBoomerRevision,
                    processName, jobId: response.entity.jobId,
                };
                const succeeded = await follow(record, pendingJob(record.jobId, processName));
                return succeeded;
            } catch (error) {
                if (isActive()) toast.error(reportError(project.projectId, error).message);
                return { succeeded: false } as const;
            } finally {
                if (isActive()) {
                    launches.delete(project.projectId);
                    setBusy(project.projectId, -1);
                }
            }
        })();
        launches.set(project.projectId, { processName, promise });
        return promise;
    };

    const restore = async (userId: string, runGeneration: number) => {
        await Promise.allSettled(readSavedProcesses(userId).map(async record => {
            try {
                const job = await fetchItem<JobDto>(ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(record.projectId));
                if (generation !== runGeneration) return;
                if (isRunningJob(job) && job.jobId === record.jobId && job.workParameters?.name === record.processName) {
                    await follow(record, job, true);
                } else {
                    // Limite acceptée : si le calcul est déjà terminé au retour, ne pas rejouer la suite.
                    removeSavedProcess(userId, record.jobId);
                }
            } catch (error) {
                if (generation === runGeneration) toast.error(reportError(record.projectId, error).message);
            }
        }));
    };

    return {
        launch,
        subscribe: (listener: () => void) => {
            listeners.add(listener);
            return () => { listeners.delete(listener); };
        },
        getSnapshot: () => snapshot,
        setUser: (userId?: string) => {
            if (snapshot.userId === userId) return;
            generation += 1;
            tasks.clear();
            launches.clear();
            completed.clear();
            counts.clear();
            publish({ userId, activeProjects: new Set(), jobs: new Map(), errors: new Map() });
            if (userId) restore(userId, generation).catch(error => console.error("Impossible de reprendre le suivi des processus.", error));
        },
    };
};

export type ProjectProcessRunner = ReturnType<typeof createProjectProcessRunner>;
export default createProjectProcessRunner;
